import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  Badge,
  Button,
  Input,
  Select,
  Code,
  useColorModeValue,
  Collapse,
  IconButton,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import { useDebugStore } from '../../../../stores/debugStore';
import styles from '../PersonalizationDebugPanel.module.css';

const EventLog: React.FC = () => {
  const { debugEvents, clearEvents } = useDebugStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const codeBgColor = useColorModeValue('gray.100', 'gray.800');

  const getEventColor = (type: string) => {
    if (type.includes('skill')) return 'blue';
    if (type.includes('concept')) return 'green';
    if (type.includes('prompt')) return 'purple';
    if (type.includes('strategy')) return 'orange';
    if (type.includes('error')) return 'red';
    return 'gray';
  };

  const toggleEventExpansion = (index: number) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedEvents(newExpanded);
  };

  const filteredEvents = debugEvents.filter(event => {
    if (filterType !== 'all' && !event.type.includes(filterType)) {
      return false;
    }
    if (searchTerm && !JSON.stringify(event).toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between">
        <Heading size="md">Live Events</Heading>
        <Button
          size="sm"
          leftIcon={<FiTrash2 />}
          variant="ghost"
          onClick={clearEvents}
          isDisabled={debugEvents.length === 0}
        >
          Clear
        </Button>
      </HStack>

      <HStack spacing={3}>
        <Input
          placeholder="Search events..."
          size="sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select
          size="sm"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          minW="150px"
        >
          <option value="all">All Events</option>
          <option value="skill">Skill</option>
          <option value="concept">Concept</option>
          <option value="prompt">Prompt</option>
          <option value="strategy">Strategy</option>
          <option value="error">Error</option>
        </Select>
      </HStack>

      {filteredEvents.length === 0 ? (
        <Box
          p={8}
          textAlign="center"
          borderWidth={1}
          borderColor={borderColor}
          borderRadius="md"
          borderStyle="dashed"
        >
          <Text color="gray.500">
            {debugEvents.length === 0 ? 'No events yet' : 'No events match filters'}
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={2}>
          {filteredEvents.map((event, index) => (
            <Box
              key={`${event.timestamp}-${index}`}
              p={3}
              bg={bgColor}
              borderRadius="md"
              borderWidth={1}
              borderColor={borderColor}
              className={index === 0 ? styles.newItem : ''}
            >
              <HStack justify="space-between" mb={2}>
                <HStack>
                  <IconButton
                    aria-label="Expand"
                    icon={expandedEvents.has(index) ? <FiChevronDown /> : <FiChevronRight />}
                    size="xs"
                    variant="ghost"
                    onClick={() => toggleEventExpansion(index)}
                  />
                  <Badge
                    colorScheme={getEventColor(event.type)}
                    variant="subtle"
                  >
                    {event.type}
                  </Badge>
                  <Text fontSize="xs" color="gray.500">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </Text>
                </HStack>
              </HStack>

              <Collapse in={expandedEvents.has(index)} animateOpacity>
                <Box
                  mt={2}
                  p={2}
                  bg={codeBgColor}
                  borderRadius="md"
                  maxH="300px"
                  overflowY="auto"
                >
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    fontSize="xs"
                    bg="transparent"
                    p={0}
                  >
                    {JSON.stringify(event.data, null, 2)}
                  </Code>
                </Box>
              </Collapse>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default EventLog;

